import { IStyle, ITextProps, Link, mergeStyles, Text, useTheme } from '@fluentui/react';
import { AppLinkToExternal, styleSummary } from 'global/Application';
import { IGCData } from 'global/Reducer/dataSlice';
import * as CRMOptionSet from "model/AttributeOptionSets.type";
import { IAccountExtended } from 'model/CRM.type';
import React, { useMemo } from 'react';
import SystemUser from './controls/SystemUser';

interface IProps {
    data: IGCData
}

const AccountSummary: React.FunctionComponent<IProps> = (props) => {

    const theme = useTheme();

    const headerStyles = { root: { fontWeight: '680', } };

    const tBodyClass = useMemo(() => {
        return mergeStyles({
            "td": {
            },
            "& > :nth-child(even)": {
                backgroundColor: theme.semanticColors.bodyDivider,
            }
        });
    }, [theme]);


    const styleSummary = useMemo(() => {
        const styleSummary: Record<string, React.CSSProperties> = {
            "dl": {
                width: '100%',
                padding: '5px',
            },
            "dt": {
                fontWeight: 680,
                textAlign: 'right',
                width: "210px",
                padding: '5px',
            },
            "dd": {
                padding: '5px',
            }
        }
        return styleSummary;
    }, []);

    const account = props?.data?.account as IAccountExtended;

    if (!account)
        return <></>;

    const isLobNA = account.bcbs_lineofbusiness === CRMOptionSet.account.bcbs_lineofbusiness["National Alliance(808900001)"].value;
    const isLobMG = account.bcbs_lineofbusiness === CRMOptionSet.account.bcbs_lineofbusiness["Major Group(808900003)"].value;


    return (
        <div style={{ flexBasis: '400px', flexGrow: 1 }}>
            <div style={{ alignSelf: 'end', margin: '0.5em', borderBottom: '1px solid gray' }}>
                <Text>Account Information</Text>
            </div>

            <table style={styleSummary.dl} >
                <tbody className={tBodyClass}>
                    <tr>
                        <td style={styleSummary.dt}>
                            <Text styles={headerStyles}>Name</Text>
                        </td>
                        <td style={styleSummary.dd}>
                            <Link
                                href={AppLinkToExternal({ type: 'account', id: account.accountid! })}
                                target="_blank"
                            >
                                {account.name}
                            </Link>
                        </td>
                    </tr>

                    <tr>
                        <td style={styleSummary.dt}>
                            <Text styles={headerStyles}>Account Type</Text>
                        </td>
                        <td style={styleSummary.dd}>
                            {account.customertypecode &&
                                <Text>{account['customertypecode@OData.Community.Display.V1.FormattedValue']}</Text>
                            }
                            {!account.customertypecode &&
                                <Text>-</Text>
                            }
                        </td>
                    </tr>

                    <tr>
                        <td style={styleSummary.dt}>
                            <Text styles={headerStyles}>Line Of Business</Text>
                        </td>
                        <td style={styleSummary.dd}>
                            {account.bcbs_lineofbusiness &&
                                <Text>{account['bcbs_lineofbusiness@OData.Community.Display.V1.FormattedValue']}</Text>
                            }
                            {!account.bcbs_lineofbusiness &&
                                <Text>-</Text>
                            }
                        </td>
                    </tr>

                    <tr>
                        <td style={styleSummary.dt}>
                            <Text styles={headerStyles}>Sales Rep</Text>
                        </td>
                        <td style={styleSummary.dd}>
                            {account._bcbs_salesrepid_value &&
                                <SystemUser
                                    id={account._bcbs_salesrepid_value}
                                    name={account['_bcbs_salesrepid_value@OData.Community.Display.V1.FormattedValue']}
                                />
                            }
                            {!account._bcbs_salesrepid_value &&
                                <Text>-</Text>
                            }
                        </td>
                    </tr>

                    {!isLobNA &&
                        <tr>
                            <td style={styleSummary.dt}>
                                <Text styles={headerStyles}>Service Rep</Text>
                            </td>
                            <td style={styleSummary.dd}>
                                {account._bcbs_servicerepid_value &&
                                    <SystemUser
                                        id={account._bcbs_servicerepid_value}
                                        name={account['_bcbs_servicerepid_value@OData.Community.Display.V1.FormattedValue']}
                                    />
                                }
                                {!account._bcbs_servicerepid_value &&
                                    <Text>-</Text>
                                }
                            </td>
                        </tr>
                    }

                    {isLobMG &&
                        <tr>
                            <td style={styleSummary.dt}>
                                <Text styles={headerStyles}>Renewal Rep</Text>
                            </td>
                            <td style={styleSummary.dd}>
                                {account._bcbs_renewalrepid_value &&
                                    <SystemUser
                                        id={account._bcbs_renewalrepid_value}
                                        name={account['_bcbs_renewalrepid_value@OData.Community.Display.V1.FormattedValue']}
                                    />
                                }
                            </td>
                        </tr>
                    }

                    {isLobMG &&
                        <tr>
                            <td style={styleSummary.dt}>
                                <Text styles={headerStyles}>Account Executive</Text>
                            </td>
                            <td style={styleSummary.dd}>
                                {account._bcbs_accountexecutiveid_value &&
                                    <SystemUser
                                        id={account._bcbs_accountexecutiveid_value}
                                        name={account['_bcbs_accountexecutiveid_value@OData.Community.Display.V1.FormattedValue']}
                                    />

                                }
                            </td>
                        </tr>
                    }

                    {isLobNA &&
                        <tr>
                            <td style={styleSummary.dt}>
                                <Text styles={headerStyles}>Client Relationship Manager</Text>
                            </td>
                            <td style={styleSummary.dd}>
                                {account._bcbs_clientrelationshipmanagerid_value &&
                                    <SystemUser
                                        id={account._bcbs_clientrelationshipmanagerid_value}
                                        name={account['_bcbs_clientrelationshipmanagerid_value@OData.Community.Display.V1.FormattedValue']}
                                    />
                                }
                                {!account._bcbs_clientrelationshipmanagerid_value &&
                                    <Text>-</Text>
                                }
                            </td>
                        </tr>
                    }

                    <tr>
                        <td style={styleSummary.dt}>
                            <Text styles={headerStyles}>Strategic Account Executive</Text>
                        </td>
                        <td style={styleSummary.dd}>
                            {account._bcbs_strategicaccountexecutiveid_value &&
                                <SystemUser
                                    id={account._bcbs_strategicaccountexecutiveid_value}
                                    name={account['_bcbs_strategicaccountexecutiveid_value@OData.Community.Display.V1.FormattedValue']}
                                />
                            }
                            {!account._bcbs_strategicaccountexecutiveid_value &&
                                <Text>-</Text>
                            }
                        </td>
                    </tr>

                    <tr>
                        <td style={styleSummary.dt}>
                            <Text styles={headerStyles}>Opportunity</Text>
                        </td>
                        <td style={styleSummary.dd}>
                            {props?.data?.opportunity &&
                                <Link
                                    href={AppLinkToExternal({ type: 'opportunity', id: props?.data?.opportunity?.opportunityid! })}
                                    target="_blank"
                                >
                                    {props?.data?.opportunity?.name}
                                </Link>
                            }
                            {!props?.data?.opportunity &&
                                <Text>-</Text>
                            }
                        </td>
                    </tr>

                    {props?.data?.bcbs_quoterequest && props.data.bcbs_quoterequest.length > 0 &&
                        <tr>
                            <td style={styleSummary.dt}>
                                <Text styles={headerStyles}>Quote Requests</Text>
                            </td>
                            <td style={styleSummary.dd}>
                                {props.data.bcbs_quoterequest.map((eachQR, index) =>
                                    <div key={eachQR.bcbs_quoterequestid}>
                                        <Link
                                            href={AppLinkToExternal({ type: 'bcbs_quoterequest', id: eachQR.bcbs_quoterequestid! })}
                                            target="_blank"
                                        >
                                            {eachQR?.bcbs_name}
                                        </Link>
                                    </div>
                                )}
                            </td>
                        </tr>
                    }

                    <tr>
                        <td style={styleSummary.dt}>
                            <Text styles={headerStyles}>Renewal Date</Text>
                        </td>
                        <td style={styleSummary.dd}>
                            {account.bcbs_anniversarydate &&
                                <Text>{account['bcbs_renewaldate@OData.Community.Display.V1.FormattedValue']}</Text>
                            }
                            {!account.bcbs_anniversarydate &&
                                <Text>-</Text>
                            }
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default AccountSummary;